import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Shell } from "@/components/Shell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import AccountSetup from "@/components/AccountSetup";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Jobs from "@/pages/Jobs";
import B2BPipeline from "@/pages/B2BPipeline";
import DetailerList from "@/pages/DetailerList";
import JobDetail from "@/pages/JobDetail";
import Stock from "@/pages/Stock";
import Profit from "@/pages/Profit";
import Studios from "@/pages/Studios";
import StudioDetail from "@/pages/StudioDetail";
import Users from "@/pages/Users";
import Warranty from "@/pages/Warranty";
import Protection from "@/pages/Protection";
import Feedback from "@/pages/Feedback";
import Inspection from "@/pages/Inspection";
import Wastage from "@/pages/Wastage";
import CentralPerformance from "@/pages/CentralPerformance";
import Accounting from "@/pages/Accounting";
import Audit from "@/pages/Audit";
import Scheduling from "@/pages/Scheduling";
import SlotChart from "@/pages/SlotChart";
import SalesStats from "@/pages/SalesStats";
import Alerts from "@/pages/Alerts";
import Orders from "@/pages/Orders";
import Dues from "@/pages/Dues";
import Configuration from "@/pages/Configuration";
import Targets from "@/pages/Targets";
import AppUpdate from "@/pages/AppUpdate";
import RoleTargets from "@/pages/RoleTargets";
import ReturnsReport from "@/pages/ReturnsReport";
import AdjustmentsLog from "@/pages/AdjustmentsLog";
import SerialTrace from "@/pages/SerialTrace";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import TrainBot from "@/pages/TrainBot";
import Statistics from "@/pages/Statistics";
import Landing from "@/pages/Landing";
import LandingAdmin from "@/pages/LandingAdmin";
import StudioDesk, { DESK_ROLES } from "@/pages/StudioDesk";
import StudioOrders from "@/pages/studio/StudioOrders";
import StudioRegistrations from "@/pages/studio/StudioRegistrations";
import StudioInspection from "@/pages/studio/StudioInspection";
import StudioCentralSales from "@/pages/studio/StudioCentralSales";
import StudioCustomers from "@/pages/studio/StudioCustomers";

const Protected = ({ children, perm, role, studio, roles }) => {
  const { user } = useAuth();
  if (user === null)
    return <div className="p-10 text-sm text-slate-500">Loading workspace…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.setup_required) return <AccountSetup />;
  if (user.role === "studio" && !studio) return <Navigate to="/studio/customers" replace />;
  if (studio && user.role !== "studio") return <Navigate to="/dashboard" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (perm && !can(user, perm)) return <Navigate to="/dashboard" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return (
    <Shell>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Shell>
  );
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user && user !== true) return <Navigate to="/dashboard" replace />;
  return <Login />;
};

const PixelPageView = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (window.fbq) window.fbq("track", "PageView");
  }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PixelPageView />
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Landing />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/landing-admin" element={<Protected role="network_admin"><LandingAdmin /></Protected>} />
            <Route
              path="/customers"
              element={<Protected perm="customers:read"><Customers /></Protected>}
            />
            <Route
              path="/customers/:id"
              element={<Protected perm="customers:read"><CustomerDetail /></Protected>}
            />
            <Route path="/jobs" element={<Protected perm="jobs:read"><Jobs /></Protected>} />
            <Route path="/b2b" element={<Protected perm="b2b:read"><B2BPipeline /></Protected>} />
            <Route path="/detailers" element={<Protected role="sales_manager_products"><DetailerList /></Protected>} />
            <Route path="/whatsapp" element={<Protected perm="customers:read"><WhatsAppInbox /></Protected>} />
            <Route path="/train-bot" element={<Protected role="network_admin"><TrainBot /></Protected>} />
            <Route path="/statistics" element={<Protected role="network_admin"><Statistics /></Protected>} />
            <Route path="/jobs/:id" element={<Protected perm="jobs:read"><JobDetail /></Protected>} />
            <Route
              path="/scheduling"
              element={<Protected perm="scheduling:read"><Scheduling /></Protected>}
            />
            <Route path="/sales-stats" element={<Protected><SalesStats /></Protected>} />
            <Route path="/slot-chart" element={<Protected><SlotChart /></Protected>} />
            <Route path="/stock" element={<Protected perm="stock:read"><Stock /></Protected>} />
            <Route path="/profit" element={<Protected perm="reports:read"><Profit /></Protected>} />
            <Route path="/audit" element={<Protected perm="audit:read"><Audit /></Protected>} />
            <Route path="/alerts" element={<Protected perm="alerts:read"><Alerts /></Protected>} />
            <Route path="/orders" element={<Protected perm="orders:read"><Orders /></Protected>} />
            <Route path="/returns-report" element={<Protected perm="orders:manage"><ReturnsReport /></Protected>} />
            <Route path="/adjustments-log" element={<Protected perm="stock:read"><AdjustmentsLog /></Protected>} />
            <Route path="/serial-trace" element={<Protected perm="orders:manage"><SerialTrace /></Protected>} />
            <Route path="/dues" element={<Protected perm="dues:read"><Dues /></Protected>} />
            <Route
              path="/configuration"
              element={<Protected perm="config:write"><Configuration /></Protected>}
            />
            <Route
              path="/targets"
              element={<Protected perm="targets:write"><Targets /></Protected>}
            />
            <Route path="/app-update" element={<Protected><AppUpdate /></Protected>} />
            <Route
              path="/monthly-targets"
              element={<Protected perm="targets:read"><RoleTargets /></Protected>}
            />
            <Route
              path="/studios"
              element={<Protected perm="studios:write"><Studios /></Protected>}
            />
            <Route
              path="/studios/:id"
              element={<Protected perm="studios:read"><StudioDetail /></Protected>}
            />
            <Route path="/users" element={<Protected perm="users:write"><Users /></Protected>} />
            <Route
              path="/team-performance"
              element={<Protected perm="performance:read"><CentralPerformance /></Protected>}
            />
            <Route
              path="/wastage"
              element={<Protected perm="reports:read"><Wastage /></Protected>}
            />
            <Route
              path="/warranties"
              element={<Protected perm="warranty:read"><Warranty /></Protected>}
            />
            <Route
              path="/protection"
              element={<Protected perm="protection:read"><Protection /></Protected>}
            />
            <Route
              path="/feedback"
              element={<Protected perm="feedback:read"><Feedback /></Protected>}
            />
            <Route
              path="/jobs/:jobId/inspection"
              element={<Protected perm="inspection:read"><Inspection /></Protected>}
            />
            <Route
              path="/accounts"
              element={<Protected perm="finance:read"><Accounting /></Protected>}
            />
            <Route path="/studio/customers" element={<Protected studio><StudioCustomers /></Protected>} />
            <Route path="/studio/central-sales" element={<Protected studio><StudioCentralSales /></Protected>} />
            <Route path="/studio/orders" element={<Protected studio><StudioOrders /></Protected>} />
            <Route path="/studio/warranty" element={<Protected studio><StudioRegistrations kind="warranty" key="w" /></Protected>} />
            <Route path="/studio/protection" element={<Protected studio><StudioRegistrations kind="plan" key="p" /></Protected>} />
            <Route path="/studio/inspection" element={<Protected studio><StudioInspection /></Protected>} />
            <Route path="/studio-desk" element={<Protected roles={DESK_ROLES}><StudioDesk /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
